// src/components/weather/WeatherSourceSelector.jsx
// Primary, first-class selector for the weather source that drives generation
// runs for one asset (PV or Wind). This is the authoritative control — the
// Weather & Generation page only *reflects* what is chosen here.
//
// Three sources, and the split between the last two is deliberate:
//
//   Platform internal  — no active dataset; the platform's own reanalysis.
//   Imported TMY       — a file the USER supplied (origin: "upload").
//   External provider  — a dataset the PLATFORM fetched from a provider such as
//                        PVGIS or CAMS (origin: "fetch").
//
// The last two are the same kind of stored object and share one activation flag,
// but they are not the same claim. "Imported" says the customer supplied the
// data; a platform-side fetch does not satisfy that, and the distinction ends up
// in feasibility-report provenance. Grouping a customer's own Meteonorm export
// together with a platform CAMS fetch also conflates two different trust
// stories. `origin` comes from the backend and cannot be derived from
// source_format, which reads 'pvgis' for both routes.
//
// It surfaces the backend's existing per-dataset activation state
// (is_active_pv / is_active_wind): no active dataset == platform internal.
// Selecting a dataset is made exclusive from the client (any other active
// dataset for this asset is deactivated first) so there is always exactly one
// source, whichever group it came from.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { activateProjectTmy, listProjectTmy } from "../../services/api";
import TmyImportManager from "./TmyImportManager";

// Must cover every source_format the backend can store, by either route -
// see weather_providers.PROVIDERS and tmy_import_service.detect_format. A missing
// key renders the raw key (e.g. "cams") in the dropdown.
const FORMAT_LABELS = {
  csv_template: "CSV template",
  pvgis: "PVGIS",
  epw: "EPW",
  solargis: "SolarGIS",
  meteonorm: "Meteonorm",
  cams: "CAMS",
  solcast: "Solcast",
  solaranywhere: "SolarAnywhere",
};

const SOURCE_PLATFORM = "platform";
const SOURCE_UPLOAD = "upload";
const SOURCE_FETCH = "fetch";

// Name and format only. The annual GHI / mean wind figures used to be appended
// here, but this control is for CHOOSING a dataset, not comparing them - and the
// numbers pushed the filename past the width of the select, truncating the only
// part that identifies which dataset it is. Comparison belongs in the Weather
// data files dialog, which shows both figures in sortable columns.
function datasetLabel(row) {
  const name = row.original_filename || row.id;
  const fmt = FORMAT_LABELS[row.source_format] || row.source_format;
  return `${name} (${fmt})`;
}

export default function WeatherSourceSelector({
  projectId,
  accessToken,
  asset = "pv",
  hasPv = true,
  hasWind = true,
  onChanged,
}) {
  const isWind = asset === "wind";
  const activeFlag = isWind ? "is_active_wind" : "is_active_pv";
  const assetWord = isWind ? "Wind" : "PV";

  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!projectId) {
      setImports([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listProjectTmy(projectId, accessToken);
      setImports(data?.imports || []);
      setError("");
    } catch (e) {
      // A load failure (e.g. the project has not been saved yet, so the
      // backend has no record of it) should not surface as an error — just
      // fall back to the Open-Meteo default with no datasets available.
      setImports([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeRow = useMemo(
    () => imports.find((r) => r[activeFlag]) || null,
    [imports, activeFlag]
  );

  // Split by provenance. Rows from before the origin column existed default to
  // "upload" server-side, so an older PVGIS fetch that predates the backfill
  // lands under Imported TMY rather than disappearing.
  const uploaded = useMemo(
    () => imports.filter((r) => (r.origin || SOURCE_UPLOAD) !== SOURCE_FETCH),
    [imports]
  );
  const fetched = useMemo(
    () => imports.filter((r) => (r.origin || SOURCE_UPLOAD) === SOURCE_FETCH),
    [imports]
  );

  const source = !activeRow
    ? SOURCE_PLATFORM
    : (activeRow.origin || SOURCE_UPLOAD) === SOURCE_FETCH
      ? SOURCE_FETCH
      : SOURCE_UPLOAD;

  // One selection per group, so switching radios does not carry a dataset from
  // the other group into a dropdown that never lists it.
  const [selectedUploadId, setSelectedUploadId] = useState("");
  const [selectedFetchId, setSelectedFetchId] = useState("");

  useEffect(() => {
    setSelectedUploadId((prev) => {
      if (source === SOURCE_UPLOAD && activeRow) return activeRow.id;
      return uploaded.some((r) => r.id === prev) ? prev : uploaded[0]?.id || "";
    });
    setSelectedFetchId((prev) => {
      if (source === SOURCE_FETCH && activeRow) return activeRow.id;
      return fetched.some((r) => r.id === prev) ? prev : fetched[0]?.id || "";
    });
  }, [activeRow, source, uploaded, fetched]);

  const setOpenMeteo = useCallback(async () => {
    if (!activeRow) return;
    setBusy(true);
    try {
      await activateProjectTmy(projectId, activeRow.id, asset, false, accessToken);
      await refresh();
      onChanged?.();
    } catch (e) {
      setError(e?.message || "Failed to switch weather source.");
    } finally {
      setBusy(false);
    }
  }, [activeRow, projectId, asset, accessToken, refresh, onChanged]);

  const setImported = useCallback(
    async (datasetId) => {
      if (!datasetId) return;
      setBusy(true);
      try {
        // Exactly one source for this asset: drop any other active dataset first.
        for (const r of imports) {
          if (r[activeFlag] && r.id !== datasetId) {
            await activateProjectTmy(projectId, r.id, asset, false, accessToken);
          }
        }
        await activateProjectTmy(projectId, datasetId, asset, true, accessToken);
        await refresh();
        onChanged?.();
      } catch (e) {
        setError(e?.message || "Failed to switch weather source.");
      } finally {
        setBusy(false);
      }
    },
    [imports, activeFlag, projectId, asset, accessToken, refresh, onChanged]
  );

  const handleSourceChange = (e) => {
    const next = e.target.value;
    if (next === SOURCE_PLATFORM) {
      setOpenMeteo();
    } else if (next === SOURCE_FETCH) {
      setImported(selectedFetchId || fetched[0]?.id || "");
    } else {
      setImported(selectedUploadId || uploaded[0]?.id || "");
    }
  };

  const renderDatasetSelect = (rows, value, onPick) => (
    <Box sx={{ pl: 4, mb: 1, maxWidth: 460 }}>
      <Select
        fullWidth
        size="small"
        value={rows.some((r) => r.id === value) ? value : ""}
        disabled={busy}
        onChange={(e) => onPick(e.target.value)}
      >
        {rows.map((row) => (
          <MenuItem key={row.id} value={row.id}>
            {datasetLabel(row)}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Weather source — {assetWord} generation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Chooses the weather series the {assetWord} generation model runs on. This is
        the setting used by generation runs; the Weather &amp; Generation page shows
        whatever is selected here.
      </Typography>

      {!projectId && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Weather datasets are stored per project. Open or save a project first, then
          choose a source here.
        </Alert>
      )}
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      <FormControl disabled={!projectId || busy}>
        <RadioGroup value={source} onChange={handleSourceChange}>
          <FormControlLabel
            value={SOURCE_PLATFORM}
            control={<Radio size="small" />}
            label="Platform internal (default)"
          />

          <FormControlLabel
            value={SOURCE_UPLOAD}
            disabled={uploaded.length === 0}
            control={<Radio size="small" />}
            label={
              uploaded.length
                ? "Imported TMY"
                : "Imported TMY (upload a file first)"
            }
          />
          {source === SOURCE_UPLOAD &&
            renderDatasetSelect(uploaded, selectedUploadId, (id) => {
              setSelectedUploadId(id);
              setImported(id);
            })}

          <FormControlLabel
            value={SOURCE_FETCH}
            disabled={fetched.length === 0}
            control={<Radio size="small" />}
            label={
              fetched.length
                ? "External provider"
                : "External provider (fetch a dataset first)"
            }
          />
          {source === SOURCE_FETCH &&
            renderDatasetSelect(fetched, selectedFetchId, (id) => {
              setSelectedFetchId(id);
              setImported(id);
            })}
        </RadioGroup>
      </FormControl>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
        <TmyImportManager
          projectId={projectId}
          accessToken={accessToken}
          hasPv={hasPv}
          hasWind={hasWind}
          onChanged={() => {
            refresh();
            onChanged?.();
          }}
        />
        {(loading || busy) && <CircularProgress size={16} />}
        <Chip
          size="small"
          variant={activeRow ? "filled" : "outlined"}
          color={activeRow ? "primary" : "default"}
          label={
            activeRow
              ? `Active: ${activeRow.original_filename || activeRow.id}`
              : "Active: Platform internal"
          }
          sx={{ fontWeight: 700 }}
        />
      </Stack>
    </Box>
  );
}
